
import React, { useState } from 'react';

interface ProfessionStepProps {
  t: any;
  isRTL: boolean;
  language: string;
  onBack: () => void;
  onHome: () => void;
  onContinue: (prof: string) => void;
}

const ProfessionStep: React.FC<ProfessionStepProps> = ({ t, isRTL, language, onBack, onHome, onContinue }) => {
  const [selected, setSelected] = useState<string>('');

  const professions = [
    { id: 'tech', label: t.tech, icon: 'code' },
    { id: 'finance', label: t.finance, icon: 'payments' },
    { id: 'healthcare', label: t.healthcare, icon: 'medical_services' },
    { id: 'edu', label: t.edu, icon: 'school' },
    { id: 'energy', label: t.energy, icon: 'factory' },
    { id: 'trade', label: t.trade, icon: 'inventory_2' },
    { id: 'media', label: t.media, icon: 'movie' },
    { id: 'other', label: t.other, icon: 'more_horiz' },
  ];

  return (
    <div className={`relative flex flex-col h-screen w-full max-w-2xl mx-auto font-display ${isRTL ? 'text-right' : 'text-left'}`}>
      <header className={`relative w-full pt-6 md:pt-10 px-8 md:px-12 pb-2 md:pb-4 flex items-center justify-between z-20`}>
        <button 
          onClick={onHome}
          className={`flex items-center gap-2.5 transition-opacity hover:opacity-80 active:scale-95 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}
        >
          <span className="material-symbols-outlined text-[#0066FF] text-[26px] md:text-[30px] font-bold">flight_takeoff</span>
          <h2 className="text-slate-dark tracking-tighter text-[20px] md:text-[22px] font-black leading-none">Settlo</h2>
        </button>
        <button 
          onClick={onBack}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-100 text-slate-soft hover:text-primary transition-all active:scale-95 group"
        >
          <span className="text-[12px] font-bold uppercase tracking-[0.1em]">{language === 'zh' ? '返回' : 'Back'}</span>
          <span className={`material-symbols-outlined text-[18px] ${isRTL ? '' : 'rotate-180'}`}>arrow_forward</span>
        </button>
      </header>

      <main className="px-8 md:px-12 pb-2 flex flex-col flex-grow pt-4 md:pt-8 overflow-y-auto no-scrollbar">
        <div className={`mb-6 md:mb-10 flex flex-col ${isRTL ? 'items-end' : 'items-start'}`}>
          <div className="text-slate-soft/60 text-[11px] font-black tracking-[0.3em] uppercase mb-3">
            {t.step}
          </div>
          <h1 className="text-[28px] md:text-[42px] font-extrabold tracking-tight leading-[1.1] text-slate-dark mb-4">
            {t.title}
          </h1>
          <p className="text-slate-soft text-[16px] md:text-[18px] leading-relaxed max-w-lg">
            {t.subtitle}
          </p>
        </div>

        <section className="flex-grow">
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            {professions.map((prof) => (
              <button
                key={prof.id}
                onClick={() => setSelected(prof.id)}
                className={`group relative flex flex-col items-center gap-3 p-4 md:p-6 rounded-stripe border transition-all duration-300 transform active:scale-[0.98] text-center ${
                  selected === prof.id
                    ? 'bg-primary/5 border-primary shadow-lg shadow-primary/10'
                    : 'bg-white border-[#E6E6E8] shadow-stripe-card hover:border-primary/30'
                }`}
              >
                <div className={`flex-none w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center transition-colors duration-300 ${
                  selected === prof.id ? 'bg-primary text-white' : 'bg-slate-50 text-slate-soft'
                }`}>
                  <span className="material-symbols-outlined text-[24px] md:text-[28px]" style={{ fontVariantLigatures: 'none' }}>
                    {prof.icon}
                  </span>
                </div>
                <span className={`text-[14px] md:text-[16px] font-bold tracking-tight ${
                  selected === prof.id ? 'text-primary' : 'text-slate-dark'
                }`}>
                  {prof.label}
                </span>
              </button>
            ))}
          </div>
        </section>
      </main>

      <footer className="w-full px-8 md:px-12 pb-8 md:pb-12 pt-4 shrink-0">
        <button 
          disabled={!selected}
          onClick={() => selected && onContinue(selected)}
          className={`group w-full relative flex items-center justify-center gap-2.5 font-black text-[17px] py-4 md:py-5 rounded-stripe shadow-stripe-button transition-all duration-300 active:scale-[0.99] ${
            selected ? 'bg-[#0066FF] text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
        >
          <span>{t.continue}</span>
        </button>
      </footer>
    </div>
  );
};

export default ProfessionStep;
